import * as _ from "lodash";
import {ASync} from "../PA.Core/Basic/ASync";
import {IdentityProvider} from "../PA.Core/Basic/Identity";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {IMessageProvider, IProgressProvider, MessageType} from "../PA.Core/PA.Core.IF.Basic";
import {IBusinessObject, IBusinessObjectChanges, IBusinessObjectChangesOptions} from "../PA.Core/PA.Core.IF.BO";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver"
import {PAdbContext} from "../PA.Core/PAdbContext";
import {
    esWB_Company,
    esWB_Customer_Role,
    esWB_Customer_Type, esWB_DistribChannel, esWB_Division,
    esWB_MasterData_Set,
    esWB_Set,
    esWB_Source
} from "../PA.Core/PAdbSchema";
import {WB_CustomerRoleRow} from "../PA.Core/PAdbSchemaRows";
import {IwbCustomerRole, IwbCustomerType} from "../PA.Core/PAdbSchemaRowsIF";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {DefaultChanges} from "../PA.Core/BusinessObject/BusinessObjectChanges";
import {SourceCustomerTypeBOValidations} from "./SourceCustomerTypeBO.Validations";

export class SourceCustomerTypeBO implements IBusinessObject {

    //<editor-fold desc = "Properties and Constructor">
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOption: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_SourceCustomerType: WB_CustomerRoleRow;

    constructor(options?: any) {
        this.CreateOptions = options || {};
        if ('dbContext' in this.CreateOptions) {
            this.dbContext = this.CreateOptions.dbContext;
        }

        this.Identity = new IdentityProvider({id: 0, name: 'New Source Customer Type'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOption = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider('Validating', this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider('Saving', this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider('Deleting', this);

        this.drWB_SourceCustomerType = null;
    }

    //</editor-fold>

    //<editor-fold desc = "IBusinessObject operations">
    get WB_SourceCustomerType(): WB_CustomerRoleRow {
        return this.drWB_SourceCustomerType;
    }

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Source Customer Type");
        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, reject) => {
            self.LoadOptions = options || {};

            if ('dbContext' in self.LoadOptions) {
                self.dbContext = self.LoadOptions.dbContext;
            } else {
                self.dbContext = new PAdbContext();
            }

            if (this.dbContext == null) {
                const sError = 'Unable to determine dbContext for new Source Customer Type.';
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            this.dbContext.createRecordWithKey(esWB_Customer_Role)
                .then((newSourceCustomerTypeRow: WB_CustomerRoleRow) => {
                    newSourceCustomerTypeRow.setDefaults();
                    self.drWB_SourceCustomerType = newSourceCustomerTypeRow;
                    self.dbContext.add(esWB_Customer_Role, newSourceCustomerTypeRow);
                    self.updateIdentity();

                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    resolve(self);
                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err);
                    reject(err);
                });
        });

        return this._OnLoadComplete;
    }

    Load(anId: number | string, options?: object): Promise<IBusinessObject> {
        const self = this;
        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start('Loading (' + self.Identity.id_ui + ')');
        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, reject) => {
            if (self.bIsLoaded) {
                const sError = 'Source Customer Type already loaded.';
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }
            self.LoadOptions = options || {};

            if ('dbContext' in self.LoadOptions) {
                self.dbContext = self.LoadOptions.dbContext;
            } else if (self.dbContext == null) {
                self.dbContext = new PAdbContext();
            }

            if (this.dbContext == null) {
                const sError = 'Unable to determine dbContext for Source Customer Type.';
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            if ('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve();
            } else {
                recordResolver = SourceCustomerTypeBO.createRecordResolver({dbContext: self.dbContext});
                recordResolverLoadPromise = SourceCustomerTypeBO.setupRecordResolver(recordResolver, [anId]);
            }

            recordResolverLoadPromise
                .then(() => {
                    const aSourceCustomerTypeRow: any = recordResolver.get(esWB_Customer_Role, anId);

                    if (_.isEmpty(aSourceCustomerTypeRow)) {
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.exception('Id not found.');
                        resolve(self);
                        return;
                    }

                    self.drWB_SourceCustomerType = aSourceCustomerTypeRow;
                    self.updateIdentity();
                })
                .then(() => {
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch((err: any) => {
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(err);
                    reject(err);
                });
        });

        return this._OnLoadComplete;
    }

    private updateIdentity(): void {
        const {
            WB_CUSTROLE_ID,
            ROLE_ID,
            ROLE_NAME
        } = this.WB_SourceCustomerType.cif;
        this.Identity.setAll( WB_CUSTROLE_ID, ROLE_ID, ROLE_NAME);
        this.LoadProgressProvider.setName(
            'Loading ' + this.Identity.name + '(' + this.Identity.id_ui + ')'
        );
        this.Identity.listenToRecordUpdates(this.WB_SourceCustomerType, 'WB_CUSTROLE_ID', 'ROLE_ID', 'ROLE_NAME');
        let userContext = PAEnvironment.getValue(PAEnvironment.UserContext, 'UserContext')
        let bHasRole = userContext.hasRoleId("Customer_Data_Editor")

        if (bHasRole)
            this.Identity.enableRecordWriteBack(this.WB_SourceCustomerType, 'ROLE_ID', 'ROLE_NAME')
        else
            this.Identity.enableRecordWriteBack(this.WB_SourceCustomerType, undefined, undefined)

    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    Validate(options?: any): Promise<IBusinessObject> {
        const self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start(
            'Validating ' + self.WB_SourceCustomerType.cif.ROLE_NAME + '(' + self.WB_SourceCustomerType.cif.ROLE_ID + ')'
        )
        return new Promise<IBusinessObject>((resolve, reject) => {
            self.ValidateOption = options || {};

            if (self.dbContext == undefined) {
                const sError = 'Unable to determine dbContext to validate Source Customer Type.';
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
            }

            SourceCustomerTypeBOValidations.ValidateFields(self).then((res) => {
                if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                    self._bIsValidated = false;
                } else {
                    self._bIsValidated = true;
                }
                self.ValidateProgressProvider.finish();
                resolve(self);

            }).catch((err) => {
                self._bIsValidated = false;
                self.ValidateProgressProvider.exception(err);
                resolve(self);

            })


        });
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbSourceCustomerType = <IwbCustomerRole>self.drWB_SourceCustomerType;
        this.SaveProgressProvider.start(
            'Saving ' + wbSourceCustomerType.ROLE_NAME + '(' + wbSourceCustomerType.ROLE_ID + ')'
        );
        return new Promise<IBusinessObject>((resolve, reject) => {
            if (self.dbContext == null) {
                self._bIsSaved = false;
                const sError = 'Unable to determine dbContext to Save Source Customer Type.';
                self.SaveProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            self.SaveOptions = options || {};

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;

            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }
            let bDone = false
            self.Validate({})
                .then((ignore) => {
                    if (!self.bIsValidated) {
                        self._bIsSaved = false;
                        self.SaveProgressProvider.finish();
                        bDone = true
                        resolve(self);
                        return
                    }
                }).then((data) => {
                if(bDone) return
                return saveFunction(esWB_Customer_Role, self.drWB_SourceCustomerType, {})})
                .then((i)=>{
                    if (bBatch) {
                        self.dbContext.addBatchCompleteListener(() => {
                            self._bIsSaved = true;
                        });
                    } else {
                        self._bIsSaved = true;
                    }

                    self.SaveProgressProvider.finish();
                    resolve(self);
                })
                .catch((err) => {
                    self.SaveProgressProvider.exception(err);
                    resolve(self);
                });
        });
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbCustomerType = <IwbCustomerType>this.drWB_SourceCustomerType;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start(
            'Deleting ' + wbCustomerType.CUSTM_NAME + '(' + wbCustomerType.CUSTM_ID + ')'
        );

        return new Promise<IBusinessObject>((resolve, reject) => {
            self.drWB_SourceCustomerType.markDeleted();

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then(() => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);
                    })
                    .catch((err) => {
                        self.DeleteProgressProvider.exception(err);
                        resolve(self);
                    });
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }


    Changes(options?: IBusinessObjectChangesOptions): IBusinessObjectChanges {
        let opts = options || {}
        opts.dctIncludeBelongsToNamedRelations = opts.dctIncludeBelongsToNamedRelations || {}
        opts.dctExcludeFields = opts.dctExcludeFields || {}

        // Set any BO Specific change settings
        return DefaultChanges(this, this.drWB_SourceCustomerType, opts)
    }

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        const opts = boLoadOptions || {};
        let dbContext: PAdbContext;
        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const customer_type_options: CreateOptions = {
            standardConditions: {
                [esWB_MasterData_Set]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_Set]: {"DELETED_IND": {"notIn": ['1']}},

            },
            dctIncludeBelongsToNamedRelations: {}
        };

        return new ESRecordResolver(dbContext, customer_type_options);
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: (string | number)[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            recordResolver.findAllWithRelationships(esWB_Customer_Role, ids)
                .then(() => {
                    resolve();
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }

    //</editor-fold>

}

PAEnvironment.registerBusinessObjectClass(SourceCustomerTypeBO, "SourceCustomerTypeBO", "Source Customer Type")